<template>
  <resource-list-wrapper
    :class="[listPage && !totalItems && !noData && !loading ? 'wrapper-move-top' : '']"
    :hidden="listPage && !totalItems && !noData && !loading"
    :back-btn-handler="() => $router.go(-2)"
    :display-back-btn="displayBackBtn"
    :title="$t('resources.footers.title')">
    <template #actions>
      <v-btn v-if="totalItems || noData || loading" class="left-icon" color="primary" to="/footers/create">
        <plus-icon class="mr-2"></plus-icon>
        {{ $t("generic.buttons.create") }}
      </v-btn>
    </template>

    <template #default>
      <delete-resource-modal :resource-name="resourceName"></delete-resource-modal>
      <va-list
        ref="list"
        :resource="resourceName"
        :filter.sync="customFiltersValues"
        :filters="filters"
        :domains-list="domainsList"
        disable-export
        disable-create
        @update:selected="getBulkSelectedItems"
        @no-data="handleNoData"
        @update:total="handleTotalItems"
        @update:loading="handleLoading"
        :create-button-label="$t('generic.buttons.create')">
        <template #no-data>
          <empty-list
            :list-page="listPage"
            :title="$t('resources.footers.list_page_empty_list_title')"
            :light-image="emptyLightImg"
            :dark-image="emptyDarkImg"
            :route="{ name: 'footers_create' }">
            <template #subtitle>
              {{ $t("resources.footers.list_page_empty_list_subtitle_1") }}
              <a
                href="https://help.siterunner.com/help/footers-how-to-create-a-footer"
                target="_blank"
                class="help-link"
                >{{ $t("resources.footers.list_page_empty_list_subtitle_link") }}</a
              >
              {{ $t("resources.footers.list_page_empty_list_subtitle_2") }}
            </template>
          </empty-list>
        </template>
        <template #no-results>
          <div class="no-results">
            {{ $t("custom.no_data") }}
          </div>
        </template>
        <template v-slot:[`bulk.actions`]>
          <v-btn
            @click="editResource('footers', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-pencil-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.edit") }}</span>
          </v-btn>
          <v-btn
            @click="duplicateResource('footers', bulkSelectedItems[0].id)"
            v-if="bulkSelectedItems.length < 2"
            text
            color="primary">
            <v-icon small>mdi-file-multiple-outline</v-icon>
            <span class="ml-2">{{ $t("generic.buttons.duplicate") }}</span>
          </v-btn>
        </template>
        <template #[`filter.search`]="props">
          <v-text-field
            class="search-filter"
            hide-details
            v-bind="props"
            label=""
            :placeholder="$t('generic.search_label')"
            @input="val => updateFiltersValues('q', val)">
          </v-text-field>
        </template>
        <va-data-table :fields="fields" class="va-table--selectable" @row-click="handleRowClick">
          <template v-slot:[`field.logo`]="{ value }">
            <img v-if="value" class="logo-img" :src="getImageUrl(value)" alt="header logo" />
          </template>
          <template #[`field.name`]="{ item }">
            <div class="no-style text--primary cell-ellipsis">
              <span>{{ item.name }}</span>
            </div>
          </template>
          <template #[`field.domain.name`]="{ item }">
            <div class="cell-ellipsis">
              <span>{{ item.domain.name }}</span>
            </div>
          </template>
          <template #[`field.updatedAt`]="{ item }">
            {{ changeDateFormat(item.updatedAt, isUserUS($store.state.user.timezone)) }}
          </template>
          <template #[`field.template`]="{ value }">
            {{ findTemplateName(value) }}
          </template>
          <template #[`cell.actions`]="{ item }">
            <DataListActions
              :actions="[
                {
                  title: 'Edit',
                  icon: 'mdi-pencil-outline',
                  function: editResource.bind(this, 'footers', item.id),
                },
                {
                  title: 'Duplicate',
                  icon: 'mdi-file-multiple-outline',
                  function: duplicateResource.bind(this, 'footers', item.id),
                },
                {
                  title: 'Delete',
                  icon: 'mdi-trash-can-outline',
                  function: deleteResource.bind(this, item),
                },
              ]">
            </DataListActions>
          </template>
        </va-data-table>
      </va-list>
    </template>
  </resource-list-wrapper>
</template>

<script>
  import definedAbility from "@/ability";
  import { changeDateFormat } from "@/@core/utils/filter";
  import { isUserUS } from "@/utility/isUserUS";
  import { FOOTER_TEMPLATES } from "../../constant/index";
  import ResourceListWrapper from "@/components/resourceWrappers/ResourceListWrapper";
  import PlusIcon from "@/components/icons/PlusIcon";
  import DataListActions from "@/components/custom/DataListActions";
  import EmptyList from "@/components/custom/EmptyList";
  import ConnectedDomainsList from "@/mixins/connectedDomainsList";
  import RowClick from "@/mixins/rowClickMixin";
  import DeleteResourceModal from "@/components/resources/DeleteResourceModal";
  // Images
  import emptyLightImg from "@/assets/images/empty-list/footer-empty-list-light.svg";
  import emptyDarkImg from "@/assets/images/empty-list/footer-empty-list-dark.svg";
  import debounce from "lodash.debounce";

  export default {
    components: {
      ResourceListWrapper,
      PlusIcon,
      DataListActions,
      EmptyList,
      DeleteResourceModal,
    },
    mixins: [ConnectedDomainsList, RowClick],
    props: {
      resourceName: {
        type: String,
        default() {
          return this.$route.meta.resource;
        },
      },
      activeFilterOnload: {
        type: Object,
        default: () => ({}),
      },
      /**
       * True if component is used in VA-admin generated route for resource list;
       * False when used elsewhere (Single domain view for example).
       */
      listPage: {
        type: Boolean,
        default: true,
      },
      domainId: {
        type: String,
        default: "",
      },
    },
    data() {
      return {
        ability: definedAbility(),
        emptyLightImg,
        emptyDarkImg,
        changeDateFormat,
        isUserUS,
        noData: false,
        loading: true,
        totalItems: 0,
        customFiltersValues: this.activeFilterOnload,
        filters: [
          {
            source: "search",
            alwaysOn: true,
          },
          {
            source: "domain",
            type: "select",
            attributes: {
              reference: "domains",
              itemText: "name",
            },
          },
        ],
        fields: [
          {
            source: "logo",
            label: this.$t("resources.footers.fields.logo"),
            width: "164px",
          },
          {
            source: "name",
            sortable: true,
            label: this.$t("resources.footers.fields.name"),
            width: "50%",
          },
          {
            source: "domain.name",
            label: this.$t("resources.pages.fields.domain"),
            sortable: true,
            width: "50%",
          },
          {
            source: "isDefault",
            type: "boolean",
            label: this.$t("resources.footers.fields.set_as_default"),
            width: "164px",
          },
          {
            source: "updatedAt",
            label: this.$t("resources.footers.fields.updated_at"),
            sortable: true,
            width: "110px",
          },
        ],
        imageUrl: process.env.VUE_APP_API,
        bulkSelectedItems: [],
        displayBackBtn: false,
      };
    },
    beforeMount() {
      this.displayBackBtn = !!this.$route.query.displayBack;

      if (this.ability.can("view", "sidebar layout column")) {
        this.fields.splice(3, 0, {
          source: "template",
          sortable: true,
          label: this.$t("resources.footers.fields.layout"),
          width: "164px",
        });
      }
    },
    methods: {
      handleNoData(val) {
        this.noData = val;
      },
      handleLoading(val) {
        this.loading = val;
      },
      handleTotalItems(val) {
        this.totalItems = val;
      },
      getBulkSelectedItems(val) {
        this.bulkSelectedItems = val;
      },
      async deleteResource(item) {
        this.$store.dispatch(`${this.resourceName}/displayDeleteModal`, {
          displayDeleteModal: true,
          items: [item],
        });
      },
      duplicateResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_create`, query: { source: resourceId } });
      },
      editResource(resource, resourceId) {
        this.$router.push({ name: `${resource}_edit`, params: { id: resourceId } });
      },
      updateFiltersValues: debounce(function (source, value) {
        this.customFiltersValues = {
          ...this.customFiltersValues,
          [source]: value,
        };
      }, 400),
      findTemplateName(templateValue) {
        if (FOOTER_TEMPLATES.find(template => template.value === templateValue)) {
          return FOOTER_TEMPLATES.find(template => template.value === templateValue).text;
        } else {
          return templateValue;
        }
      },
      getImageUrl(value) {
        if (this.ability.can("upload", "media object to inulti server")) {
          return this.imageUrl + value.contentUrl;
        } else {
          return value.contentUrl;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .logo-img {
    max-width: 132px;
    vertical-align: middle;
    max-height: 38px;
  }
</style>
